import React, { createContext, use, useMemo } from 'react'
import {
  SchedulePublishedViewSchedule,
  WorkInterval,
  WorkingHours,
} from '@chilipiper/api-client-user-availability'
import { AvailabilitySchedule, useAvailabilitySchedules } from '../../api/user-availability'

type AvailabilityWorkingHours = {
  isStrict: boolean
  origin: SchedulePublishedViewSchedule
  value: WorkInterval
}

export type AvailabilitiesWorkingHours = {
  [K in keyof WorkingHours]-?: Array<AvailabilityWorkingHours>
}

const availabilitiesWorkingHoursContext = createContext<AvailabilitiesWorkingHours | undefined>(
  undefined
)

const emptyWorkingHours: AvailabilitiesWorkingHours = {
  sunday: [],
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
}

export const availabilityScheduleToWorkingHours = (
  availabilitySchedule: AvailabilitySchedule
): AvailabilitiesWorkingHours => {
  const published = availabilitySchedule.published

  if (published?.schedule.type !== 'CustomSchedule') {
    return emptyWorkingHours
  }

  const days = Object.keys(published.schedule.workingHours ?? {}) as Array<keyof WorkingHours>

  const isStrict = published.schedule.isStrictlyReserved

  return days.reduce((workingHours, day) => {
    workingHours[day] =
      published.schedule.workingHours[day]?.map(item => ({
        value: item,
        origin: published,
        isStrict,
      })) ?? []
    return workingHours
  }, structuredClone(emptyWorkingHours))
}

export const AvailabilitiesWorkingHoursProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: availabilitySchedules } = useAvailabilitySchedules(true)

  const value = useMemo(() => {
    return availabilitySchedules?.reduce((workingHours, availability) => {
      const scheduleWorkingHours = availabilityScheduleToWorkingHours(availability)

      Object.keys(scheduleWorkingHours).forEach(day => {
        workingHours[day] = [...workingHours[day], ...scheduleWorkingHours[day]]
      })

      return workingHours
    }, structuredClone(emptyWorkingHours))
  }, [availabilitySchedules])

  return (
    <availabilitiesWorkingHoursContext.Provider value={value}>
      {children}
    </availabilitiesWorkingHoursContext.Provider>
  )
}

export const useAvailabilitiesWorkingHours = () => {
  return use(availabilitiesWorkingHoursContext)
}
